<!-- PS-6586: SIP order link, embedded in "Own number on outbound calls" on the Phone Service page (PS-6598).
     A quiet inner panel: header (title + link status), one action line (send button OR not-needed note), history.
     Disabled-action hint = inline explainer under the button, not a tooltip (Style Guide §17.2). -->
<section class="flex flex-col gap-3 rounded-lg border border-border/50 bg-muted/40 p-4"
         aria-labelledby="ua-sip-order-link-title" data-testid="ua-sip-order-link-section">
  <h4 id="ua-sip-order-link-title" class="cmt-eyebrow">SIP Order Link</h4>

  @if (showSendButton()) {
    <div class="flex flex-col items-start gap-2">
      <!-- Recipient: only while there is something to book; maintained on the User Account page. -->
      <div class="flex w-full flex-wrap items-center gap-x-3 gap-y-2" data-testid="ua-sip-order-link-recipient">
        <span class="text-sm text-muted-foreground">To</span>
        <span class="min-w-0 break-all text-base font-medium text-foreground">{{ userAccount().email || '—' }}</span>
        @if (userAccount().email) {
          <hlm-badge [variant]="userAccount().emailConfirmed ? 'success' : 'accent'">
            {{ userAccount().emailConfirmed ? 'Confirmed' : 'Not confirmed' }}
          </hlm-badge>
        }
        <button type="button" hlmBtn variant="outline" size="sm" class="ml-auto" (click)="openUserAccount.emit()"
                data-testid="ua-sip-order-link-user-account-btn">
          <hlm-icon name="lucideUserRound" size="0.9rem" />
          User Account
        </button>
      </div>
      @if (books().length) {
        <!-- What the link books: the most important fact of the block, so it gets its own surface (PS-6598). -->
        <div class="w-full rounded-md border border-border bg-card" data-testid="ua-sip-order-link-books">
          <p class="border-b border-border/70 px-3 py-2 text-sm text-foreground">
            The link books what is missing. When the customer confirms:
          </p>
          <dl class="flex flex-col divide-y divide-border/70">
            @for (item of books(); track item.label) {
              <div class="flex items-center justify-between gap-4 px-3 py-2">
                <dt class="text-base font-medium text-foreground">{{ item.label }}</dt>
                <dd>
                  <hlm-badge [variant]="item.state === 'done' ? 'success' : item.state === 'missing' ? 'destructive' : 'outline'"
                             [attr.data-state]="item.state">
                    {{ item.state === 'done' ? 'Done' : item.state === 'missing' ? 'Missing' : 'Unknown' }}
                  </hlm-badge>
                </dd>
              </div>
            }
          </dl>
        </div>
      } @else {
        <p class="text-sm leading-snug text-foreground">Emails the customer a personal link to book the SIP account themselves.</p>
      }
      <button type="button" hlmBtn variant="default" class="gap-2"
              (click)="send()"
              [disabled]="!!disabledReason() || sending()"
              [attr.aria-describedby]="disabledReason() ? 'ua-sip-order-link-hint' : null"
              data-testid="ua-sip-order-link-send-btn">
        @if (sending()) {
          <hlm-spinner size="16" class="text-current" />
          Sending…
        } @else {
          <hlm-icon name="lucideSend" size="0.9rem" />
          {{ sendLabel() }}
        }
      </button>
      @if (disabledReason()) {
        <p id="ua-sip-order-link-hint" class="max-w-[22rem] text-sm leading-snug text-foreground"
           data-testid="ua-sip-order-link-disabled-hint">Disabled — {{ disabledReason() }}.</p>
      }
    </div>
  } @else if (notNeededReason() && status() !== 'ordered') {
    <p class="flex items-start gap-2 text-sm leading-snug text-foreground">
      <hlm-icon name="lucideCircleCheck" size="1rem" class="mt-px flex-none text-success" />
      <span data-testid="ua-sip-order-link-not-needed">Not needed — {{ notNeededReason() }}.</span>
    </p>
  }

  <div class="border-t border-border/70 pt-3">
    @switch (historyState()) {
      @case ('loading') {
        <div class="flex items-center gap-3 text-sm text-muted-foreground" data-testid="ua-sip-order-link-history-loading">
          <hlm-spinner size="18" />
          Loading link history…
        </div>
      }
      @case ('error') {
        <hlm-alert tone="error" class="items-center" data-testid="ua-sip-order-link-history-error">
          <hlm-icon name="lucideCircleAlert" size="1rem" class="text-destructive" />
          <span class="flex-1">SIP order link history could not be loaded.</span>
          <button type="button" hlmBtn variant="outline" size="sm" (click)="reloadHistory()"
                  data-testid="ua-sip-order-link-history-retry">
            <hlm-icon name="lucideRefreshCw" size="0.9rem" />
            Retry
          </button>
        </hlm-alert>
      }
      @default {
        @if (rows().length === 0) {
          <p class="text-sm text-muted-foreground" data-testid="ua-sip-order-link-history-empty">
            No SIP order link sent in the last {{ historyDays }} days.
          </p>
        } @else {
          <ol class="flex flex-col gap-2.5"
              aria-label="SIP order link history"
              data-testid="ua-sip-order-link-history">
            @for (row of rows(); track rowKey(row)) {
              <li class="grid grid-cols-[1rem_1fr_auto] items-start gap-x-2.5 text-sm text-foreground"
                  [class.pl-6]="row.kind !== 'sent'"
                  data-testid="ua-sip-order-link-history-row"
                  [attr.data-kind]="row.kind">
                @switch (row.kind) {
                  @case ('sent') {
                    <hlm-icon name="lucideSend" size="0.95rem" class="mt-0.5 text-muted-foreground" />
                    <div class="flex flex-col">
                      <span>Sent <span class="font-mono-feat">{{ row.at | date: dateFormat }}</span> by {{ employeeLabel(row.issuedBy) }}</span>
                      @if (row.expiresAt) {
                        <span class="text-muted-foreground">Valid until <span class="font-mono-feat">{{ row.expiresAt | date: dateFormat }}</span></span>
                      }
                    </div>
                  }
                  @case ('opened') {
                    <hlm-icon name="lucideEye" size="0.95rem" class="mt-0.5 text-muted-foreground" />
                    @if (row.openCount > 1) {
                      <span>
                        Opened {{ row.openCount }}× <span class="text-muted-foreground">(last
                        <span class="font-mono-feat">{{ row.at | date: dateFormat }}</span>)</span>
                      </span>
                    } @else {
                      <span>Opened <span class="font-mono-feat">{{ row.at | date: dateFormat }}</span></span>
                    }
                  }
                  @case ('ordered') {
                    <hlm-icon name="lucideCircleCheck" size="0.95rem" class="mt-0.5 text-success" />
                    <div class="flex flex-col">
                      <span>Ordered <span class="font-mono-feat">{{ row.at | date: dateFormat }}</span></span>
                      @if (row.result === alreadyExists) {
                        <span class="text-muted-foreground">The account already had a SIP account</span>
                      }
                    </div>
                  }
                }
                <!-- The status sits on the row of the link it describes (newest sent, or the order). -->
                @if (rowKey(row) === statusRowKey() && statusBadge(); as badge) {
                  <hlm-badge [variant]="badge.variant" class="col-start-3 row-start-1"
                             data-testid="ua-sip-order-link-status-badge">{{ badge.label }}</hlm-badge>
                }
              </li>
            }
          </ol>
        }
      }
    }
  </div>
</section>
